import React from 'react';
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { ButtonRounded } from '../UISystem/Buttons/ButtonRounded';
import { SHADOWS } from '../../styles/UISystem';
import { getInitials } from '../../utils/connectionName';
import { firstNameOf } from '../../utils/contactUtils';

const ProximityCard = ({ state, nearbyUser, onConnect, onDismiss }) => {
  if (!nearbyUser || state === 'idle') return null;

  const { name, photoUrl } = nearbyUser;
  const initials = getInitials(name);
  const isLoading = state === 'invite_sent' || state === 'connecting';
  const isError = state === 'error';

  return (
    <>
      {/* Backdrop — tap to dismiss all cards */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={state === 'connecting' ? undefined : onDismiss}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 199,
          cursor: state === 'connecting' ? 'default' : 'pointer',
        }}
      />

      {/* Card */}
      <motion.div
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -80, opacity: 0 }}
        transition={{
          type: 'spring',
          damping: 22,
          stiffness: 120,
          mass: 0.8,
        }}
        style={{
          position: 'fixed',
          top: 60,
          left: 16,
          right: 16,
          zIndex: 200,
          backgroundColor: '#FFFFFF',
          borderRadius: 28,
          boxShadow: SHADOWS.TIER4,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Photo */}
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: '#C7C7CC',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <span style={{ fontSize: 17, fontWeight: 600, color: '#FFFFFF' }}>{initials}</span>
          )}
        </div>

        {/* Name + Subtitle (centered between photo and action area) */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: '#000000',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {firstNameOf(name)}
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: isError ? '#FF3B30' : '#8E8E93',
              lineHeight: 1.2,
            }}
          >
            {isError ? "Couldn't connect" : 'Nearby'}
          </span>
        </div>

        {/* Action area */}
        <div
          style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, marginLeft: 12 }}
        >
          {state === 'nearby_detected' && (
            <ButtonRounded text="Connect" size="auto" variant="black" onClick={onConnect} />
          )}
          {isLoading && (
            <div
              style={{
                width: 28,
                height: 28,
                border: '3px solid #E5E5E5',
                borderTopColor: '#000000',
                borderRadius: '50%',
                animation: 'proximityCardSpin 0.8s linear infinite',
              }}
            />
          )}
          {isError && (
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: '#8E8E93',
                whiteSpace: 'nowrap',
              }}
            >
              Try the QR scanner
            </span>
          )}
        </div>

        {/* Dismiss — small text X (shared close affordance with WelcomeInviteBanner;
            see ui-design-system.md § Action bars). Visible except while connecting. */}
        {state !== 'connecting' && (
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            style={{
              flexShrink: 0,
              marginLeft: 12,
              background: 'none',
              border: 'none',
              padding: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={16} weight="bold" color="#C7C7CC" />
          </button>
        )}
      </motion.div>

      {/* Spinner keyframes */}
      <style>{`
        @keyframes proximityCardSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ProximityCard;
